import { useEffect, useState } from 'react';
import { Save, Plus, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';

type Props = {
  sellerOrderId: string;
  initialNotes: string | null;
  initialTags: string[];
  onSaved?: () => void;
};

/** Sipariş not ve etiket düzenleyici.
 *  Kayıt sırasında orders tablosunu update eder + audit_log tablosuna iz bırakır.
 */
export default function NotesEditor({ sellerOrderId, initialNotes, initialTags, onSaved }: Props) {
  const [notes, setNotes] = useState(initialNotes ?? '');
  const [tags, setTags] = useState<string[]>(initialTags ?? []);
  const [newTag, setNewTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setNotes(initialNotes ?? '');
    setTags(initialTags ?? []);
    setDirty(false);
  }, [sellerOrderId, initialNotes, initialTags]);

  function addTag() {
    const t = newTag.trim();
    if (!t || tags.includes(t)) return;
    setTags([...tags, t]);
    setNewTag('');
    setDirty(true);
  }

  function removeTag(t: string) {
    setTags(tags.filter((x) => x !== t));
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error: updErr } = await supabase
        .from('orders')
        .update({ notes: notes || null, tags })
        .eq('seller_order_id', sellerOrderId);
      if (updErr) throw updErr;

      // Audit log — INSERT kayıt
      if (user) {
        const auditEntries: Array<{
          seller_order_id: string;
          user_id: string;
          user_email: string | undefined;
          field: string;
          old_value: unknown;
          new_value: unknown;
          action: string;
        }> = [];
        if ((initialNotes ?? '') !== notes) {
          auditEntries.push({
            seller_order_id: sellerOrderId,
            user_id: user.id,
            user_email: user.email,
            field: 'notes',
            old_value: initialNotes,
            new_value: notes,
            action: 'note_set',
          });
        }
        if (JSON.stringify(initialTags ?? []) !== JSON.stringify(tags)) {
          auditEntries.push({
            seller_order_id: sellerOrderId,
            user_id: user.id,
            user_email: user.email,
            field: 'tags',
            old_value: initialTags,
            new_value: tags,
            action: 'tags_update',
          });
        }
        if (auditEntries.length > 0) {
          await supabase.from('audit_log').insert(auditEntries);
        }
      }

      setSavedAt(new Date());
      setDirty(false);
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-medium text-slate-600 mb-1 block">Notlar</label>
        <textarea
          value={notes}
          onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
          rows={3}
          placeholder="Sipariş hakkında not ekleyin (örn. müşteri telefonu aradı, kargo gecikti)..."
          className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-slate-500"
        />
      </div>

      <div>
        <label className="text-xs font-medium text-slate-600 mb-1 block">Etiketler</label>
        <div className="flex flex-wrap gap-1 mb-2">
          {tags.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2 py-0.5 text-xs">
              {t}
              <button type="button" onClick={() => removeTag(t)} className="hover:text-indigo-900">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {tags.length === 0 && <span className="text-xs text-slate-400">Etiket yok</span>}
        </div>
        <div className="flex gap-1">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
            placeholder="yeni etiket..."
            className="flex-1 px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-slate-500"
          />
          <button type="button" onClick={addTag} className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-xs rounded inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> ekle
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-medium px-3 py-1.5 rounded"
        >
          <Save className="w-3.5 h-3.5" />
          {saving ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
        {savedAt && !dirty && (
          <span className="text-[10px] text-emerald-600">
            Kaydedildi: {savedAt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
        {error && <span className="text-[10px] text-rose-600">{error}</span>}
      </div>
    </div>
  );
}
